import { useEffect } from 'react';

/**
 * Hook global que habilita arrastar para rolar horizontalmente em qualquer
 * container com overflow-x: auto que possua conteúdo maior que o visível.
 * Basta chamar uma vez no DashboardLayout — sem necessidade de alterar
 * cada tabela individualmente.
 */
export function useDragScroll() {
  useEffect(() => {
    let activeEl: HTMLElement | null = null;
    let startX = 0;
    let scrollLeft = 0;
    let isDragging = false;

    /** Verifica se o elemento (ou algum ancestral até o container scrollável)
     *  é interativo, para não interferir em cliques em botões/inputs/links. */
    const isInteractive = (target: EventTarget | null, scrollable: HTMLElement): boolean => {
      let node = target as HTMLElement | null;
      while (node && node !== scrollable) {
        const tag = node.tagName?.toLowerCase();
        if (
          tag === 'button' ||
          tag === 'input' ||
          tag === 'select' ||
          tag === 'textarea' ||
          tag === 'a' ||
          node.getAttribute('role') === 'button' ||
          node.getAttribute('role') === 'link' ||
          node.getAttribute('contenteditable') === 'true'
        ) {
          return true;
        }
        node = node.parentElement;
      }
      return false;
    };

    /** Percorre a árvore DOM para cima a partir do target procurando o primeiro
     *  elemento com overflow-x: auto que tenha conteúdo realmente mais largo
     *  do que sua área visível. */
    const findScrollable = (target: EventTarget | null): HTMLElement | null => {
      let node = target as HTMLElement | null;
      while (node) {
        const style = getComputedStyle(node);
        const overflowX = style.overflowX;
        if (
          (overflowX === 'auto' || overflowX === 'scroll') &&
          node.scrollWidth > node.clientWidth
        ) {
          return node;
        }
        node = node.parentElement;
      }
      return null;
    };

    const onMouseDown = (e: MouseEvent) => {
      // Apenas botão primário (esquerdo)
      if (e.button !== 0) return;

      const scrollable = findScrollable(e.target);
      if (!scrollable) return;

      // Não ativa drag em elementos interativos
      if (isInteractive(e.target, scrollable)) return;

      activeEl = scrollable;
      isDragging = true;
      startX = e.clientX;
      scrollLeft = scrollable.scrollLeft;
      scrollable.style.cursor = 'grabbing';
      scrollable.style.userSelect = 'none';
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!isDragging || !activeEl) return;
      const walk = e.clientX - startX;
      activeEl.scrollLeft = scrollLeft - walk;
    };

    const stopDragging = () => {
      if (!activeEl) return;
      isDragging = false;
      activeEl.style.cursor = '';
      activeEl.style.userSelect = '';
      activeEl = null;
    };

    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', stopDragging);
    // Também para se o ponteiro sair da janela
    document.addEventListener('mouseleave', stopDragging);

    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', stopDragging);
      document.removeEventListener('mouseleave', stopDragging);
    };
  }, []);
}
